import { useEffect, useRef, useState } from 'react';
import {
  CATEGORY_LABEL,
  OPTIONS,
  type GameSnapshot,
  type OptionLetter,
} from '@hypevault/hype-million';
import { DEFAULT_GAME_SOUND_CONFIG } from '@hypevault/shared-types';
import { gameSounds } from '../lib/gameSounds';

/**
 * Hype Million — full-screen Millionaire-style overlay (1920×1080 native).
 *
 * Phases:
 *   - voting : question + 4 option pods + ladder rail + live vote bars
 *   - reveal : lock-in flash → green/red on correct/wrong + payout splash
 *   - ended  : terminal splash (jackpot / game over / no-votes / aborted)
 */

interface Fields {
  position?: 'fullscreen';
  scale?: number;
  opacity?: number;
}

interface Props {
  streamerId: string;
  snapshot: GameSnapshot | null;
  fields: Fields;
}

const PALETTE = {
  bg: '#020617',
  bgInner: '#0b1530',
  gold: '#f5b942',
  goldDeep: '#d97706',
  blue: '#1d4ed8',
  blueGlow: '#3b82f6',
  green: '#22c55e',
  red: '#ef4444',
  text: '#fff',
  textDim: 'rgba(255,255,255,0.65)',
};

const ANIMATIONS = `
@keyframes hm-fadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes hm-spotlight { 0% { opacity: 0.3 } 50% { opacity: 0.55 } 100% { opacity: 0.3 } }
@keyframes hm-pulse { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.04) } }
@keyframes hm-glow { 0%, 100% { box-shadow: 0 0 24px 4px rgba(245,185,66,0.55) } 50% { box-shadow: 0 0 56px 12px rgba(245,185,66,0.95) } }
@keyframes hm-rise { from { transform: translateY(40px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
@keyframes hm-flashLock { 0% { transform: scale(1); box-shadow: 0 0 0 rgba(59,130,246,0); } 50% { transform: scale(1.06); box-shadow: 0 0 64px 16px rgba(59,130,246,0.9); } 100% { transform: scale(1); box-shadow: 0 0 0 rgba(59,130,246,0); } }
@keyframes hm-flashWin { 0% { transform: scale(1); background: linear-gradient(180deg,#0c1d3a,#091433); } 50% { transform: scale(1.08); background: linear-gradient(180deg,#14532d,#052e16); } 100% { background: linear-gradient(180deg,#14532d,#052e16); transform: scale(1); } }
@keyframes hm-flashLose { 0% { transform: scale(1); background: linear-gradient(180deg,#0c1d3a,#091433); } 50% { transform: scale(1.06); background: linear-gradient(180deg,#7f1d1d,#450a0a); } 100% { background: linear-gradient(180deg,#7f1d1d,#450a0a); transform: scale(1); } }
@keyframes hm-confetti { 0% { transform: translateY(-20px) rotate(0); opacity: 1 } 100% { transform: translateY(1080px) rotate(720deg); opacity: 0 } }
@keyframes hm-shake { 0%,100% { transform: translateX(0) } 20% { transform: translateX(-12px) } 40% { transform: translateX(10px) } 60% { transform: translateX(-8px) } 80% { transform: translateX(6px) } }
@keyframes hm-coin { 0% { transform: translateY(-30px) rotateZ(0deg) scale(0.7); opacity: 0 } 20% { opacity: 1 } 100% { transform: translateY(900px) rotateZ(900deg) scale(1); opacity: 0 } }
`;

export function HypeMillionWidget({ streamerId, snapshot, fields }: Props) {
  const initial = useRef<GameSnapshot | null>(snapshot);
  const [latest, setLatest] = useState<GameSnapshot | null>(snapshot);
  const prevRef = useRef<GameSnapshot | null>(snapshot);
  useEffect(() => {
    if (initial.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    const ac = new AbortController();
    fetch(`/api/hype-million/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (json: { snapshot?: GameSnapshot; sound?: typeof DEFAULT_GAME_SOUND_CONFIG } | null) => {
          if (json?.snapshot) setLatest(json.snapshot);
          gameSounds.configure(json?.sound ?? DEFAULT_GAME_SOUND_CONFIG);
        },
      )
      .catch(() => undefined);
    return () => ac.abort();
  }, []);
  useEffect(() => {
    if (snapshot) setLatest(snapshot);
  }, [snapshot]);
  // Unified sound triggers (no-op until pack assets register).
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = latest;
    if (!latest) return;
    if (!prev) {
      void gameSounds.play('game_start');
      return;
    }
    if (prev.totalVotes < latest.totalVotes) {
      void gameSounds.play('vote_received');
    }
    if (prev.phase !== latest.phase) {
      if (latest.phase === 'reveal') {
        void gameSounds.play('reveal');
        if (latest.lockedAnswer && latest.correctAnswer) {
          void gameSounds.play(
            latest.lockedAnswer === latest.correctAnswer ? 'correct' : 'incorrect',
          );
        }
      } else if (latest.phase === 'ended' && latest.endState) {
        void gameSounds.play(latest.endState.reason === 'win' ? 'winner' : 'loser');
        if (latest.endState.totalGemsAwarded > 0) {
          setTimeout(() => {
            void gameSounds.play('celebration');
            void gameSounds.play('gems_awarded');
          }, 500);
        }
      }
    }
  }, [latest]);
  void streamerId;

  const opacity = typeof fields.opacity === 'number' ? fields.opacity : 1;
  const scale = typeof fields.scale === 'number' ? fields.scale : 1;

  if (!latest) {
    return <IdleStage opacity={opacity} />;
  }
  return <Stage snapshot={latest} opacity={opacity} scale={scale} />;
}

function IdleStage({ opacity }: { opacity: number }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        opacity,
        pointerEvents: 'none',
      }}
    />
  );
}

function Stage({
  snapshot,
  opacity,
  scale,
}: {
  snapshot: GameSnapshot;
  opacity: number;
  scale: number;
}) {
  const phase = snapshot.phase;
  const isReveal = phase === 'reveal' || phase === 'ended';
  const isWinReveal = isReveal && snapshot.correct === true;
  const isLoseReveal = isReveal && snapshot.correct === false;
  const totalVotes = snapshot.totalVotes;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width: 1920,
        height: 1080,
        opacity,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        animation: 'hm-fadeIn 400ms ease both',
        background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
        color: PALETTE.text,
        fontFamily:
          '"Rajdhani","Orbitron",ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        overflow: 'hidden',
      }}
    >
      <style>{ANIMATIONS}</style>

      {/* Stage spotlight effect */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(245,185,66,0.18) 0%, transparent 50%)',
          animation: 'hm-spotlight 6s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      {/* End splash takes over the entire stage */}
      {phase === 'ended' && snapshot.endState ? (
        <EndSplash snapshot={snapshot} />
      ) : (
        <>
          {/* Header: level badge + category + timer */}
          <Header snapshot={snapshot} totalVotes={totalVotes} />

          {/* Center: question + options */}
          <main
            style={{
              position: 'absolute',
              left: 80,
              right: 380,
              top: 200,
              bottom: 80,
              display: 'flex',
              flexDirection: 'column',
              gap: 40,
            }}
          >
            <QuestionCard snapshot={snapshot} />
            <OptionsGrid snapshot={snapshot} />
            {isReveal && snapshot.payout && (
              <PayoutBanner
                payout={snapshot.payout}
                isWin={isWinReveal}
                prize={snapshot.prizeGems}
              />
            )}
          </main>

          {/* Right rail: ladder */}
          <LadderRail snapshot={snapshot} />

          {/* Win confetti */}
          {isWinReveal && <Confetti />}
          {/* Lose shake vignette */}
          {isLoseReveal && <LoseVignette />}
        </>
      )}
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────

function Header({ snapshot, totalVotes }: { snapshot: GameSnapshot; totalVotes: number }) {
  const phase = snapshot.phase;
  const isVoting = phase === 'voting';
  const secondsLeft = isVoting
    ? Math.max(0, Math.ceil((snapshot.deadline - Date.now()) / 1000))
    : 0;
  return (
    <header
      style={{
        position: 'absolute',
        top: 40,
        left: 80,
        right: 380,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
      }}
    >
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 12,
            background: `linear-gradient(135deg, ${PALETTE.gold}, ${PALETTE.goldDeep})`,
            display: 'grid',
            placeItems: 'center',
            fontSize: 36,
            color: '#000',
            fontWeight: 900,
            boxShadow: '0 8px 32px rgba(245,185,66,0.45)',
          }}
        >
          $
        </div>
        <div>
          <div
            style={{
              fontSize: 14,
              letterSpacing: 4,
              color: PALETTE.gold,
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            Hype
          </div>
          <div
            style={{
              fontSize: 32,
              fontWeight: 900,
              letterSpacing: 2,
              lineHeight: 1,
              fontFamily: '"Orbitron","Rajdhani",sans-serif',
            }}
          >
            MILLION
          </div>
        </div>
      </div>

      {/* Category + level chip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <Chip
          label={`Q${snapshot.level} / ${snapshot.totalLevels}`}
          value={`💎 ${snapshot.prizeGems.toLocaleString()}`}
        />
        <Chip label="Category" value={CATEGORY_LABEL[snapshot.category] ?? 'Mixed'} />
        <Chip label="Votes" value={totalVotes.toString()} />
      </div>

      {/* Timer */}
      {isVoting && <TimerRing secondsLeft={secondsLeft} totalSeconds={Math.max(secondsLeft, 30)} />}
    </header>
  );
}

function Chip({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: '10px 18px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(245,185,66,0.35)',
        borderRadius: 999,
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 2,
          color: PALETTE.textDim,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: PALETTE.gold }}>{value}</div>
    </div>
  );
}

function TimerRing({ secondsLeft, totalSeconds }: { secondsLeft: number; totalSeconds: number }) {
  const pct = Math.max(0, Math.min(1, secondsLeft / totalSeconds));
  const urgent = secondsLeft <= 5;
  const r = 36;
  const c = 2 * Math.PI * r;
  return (
    <div
      style={{
        position: 'relative',
        width: 96,
        height: 96,
        display: 'grid',
        placeItems: 'center',
        animation: urgent ? 'hm-pulse 600ms ease-in-out infinite' : undefined,
      }}
    >
      <svg
        width={96}
        height={96}
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <circle cx={48} cy={48} r={r} stroke="rgba(255,255,255,0.12)" strokeWidth={6} fill="none" />
        <circle
          cx={48}
          cy={48}
          r={r}
          stroke={urgent ? PALETTE.red : PALETTE.gold}
          strokeWidth={6}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 500ms linear' }}
        />
      </svg>
      <div
        style={{
          fontSize: 32,
          fontWeight: 900,
          color: urgent ? PALETTE.red : PALETTE.gold,
          fontFamily: '"Orbitron",sans-serif',
        }}
      >
        {secondsLeft}
      </div>
    </div>
  );
}

// ─── Question + options ─────────────────────────────────────────────────

function QuestionCard({ snapshot }: { snapshot: GameSnapshot }) {
  return (
    <div
      style={{
        padding: '32px 48px',
        background: 'linear-gradient(180deg, rgba(11,21,48,0.85), rgba(2,6,23,0.95))',
        border: `2px solid rgba(245,185,66,0.55)`,
        borderRadius: 24,
        boxShadow: '0 24px 64px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
        animation: 'hm-rise 500ms ease both',
      }}
    >
      <div
        style={{
          fontSize: 12,
          letterSpacing: 4,
          color: PALETTE.gold,
          textTransform: 'uppercase',
          marginBottom: 8,
          fontWeight: 700,
        }}
      >
        For {snapshot.prizeGems.toLocaleString()} 💎 — the question is…
      </div>
      <div
        style={{
          fontSize: 44,
          fontWeight: 700,
          lineHeight: 1.25,
          color: PALETTE.text,
          textShadow: '0 4px 24px rgba(0,0,0,0.45)',
        }}
      >
        {snapshot.question}
      </div>
    </div>
  );
}

function OptionsGrid({ snapshot }: { snapshot: GameSnapshot }) {
  const phase = snapshot.phase;
  const total = Math.max(1, snapshot.totalVotes);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 24,
      }}
    >
      {OPTIONS.map((letter, i) => {
        const text = snapshot.options[i] ?? '';
        const votes = snapshot.tally[letter] ?? 0;
        const pct = Math.round((votes / total) * 100);
        const isLocked =
          snapshot.lockedAnswer === letter && (phase === 'reveal' || phase === 'ended');
        const isCorrect =
          snapshot.correctAnswer === letter && (phase === 'reveal' || phase === 'ended');
        return (
          <OptionPod
            key={letter}
            letter={letter}
            text={text}
            votes={votes}
            pct={pct}
            isLocked={isLocked}
            isCorrect={isCorrect}
            phase={phase}
          />
        );
      })}
    </div>
  );
}

function OptionPod({
  letter,
  text,
  votes,
  pct,
  isLocked,
  isCorrect,
  phase,
}: {
  letter: OptionLetter;
  text: string;
  votes: number;
  pct: number;
  isLocked: boolean;
  isCorrect: boolean;
  phase: GameSnapshot['phase'];
}) {
  const isReveal = phase === 'reveal' || phase === 'ended';
  const showCounts = isReveal || true; // show live during voting too
  const borderColor =
    isReveal && isCorrect
      ? PALETTE.green
      : isReveal && isLocked && !isCorrect
        ? PALETTE.red
        : 'rgba(245,185,66,0.45)';
  const animation = isLocked && phase === 'reveal' ? 'hm-flashLock 800ms ease 1' : undefined;
  return (
    <div
      style={{
        position: 'relative',
        padding: '20px 24px',
        background:
          isReveal && isCorrect
            ? 'linear-gradient(180deg, rgba(34,197,94,0.25), rgba(20,83,45,0.45))'
            : isReveal && isLocked && !isCorrect
              ? 'linear-gradient(180deg, rgba(239,68,68,0.25), rgba(127,29,29,0.45))'
              : 'linear-gradient(180deg, rgba(29,78,216,0.18), rgba(11,21,48,0.65))',
        border: `2px solid ${borderColor}`,
        borderRadius: 18,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        minHeight: 92,
        overflow: 'hidden',
        animation,
      }}
    >
      {/* Live vote-bar background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(90deg, ${
            isReveal && isCorrect
              ? 'rgba(34,197,94,0.18)'
              : isReveal && isLocked && !isCorrect
                ? 'rgba(239,68,68,0.18)'
                : 'rgba(59,130,246,0.18)'
          } 0%, transparent 100%)`,
          width: `${pct}%`,
          transition: 'width 400ms ease',
          pointerEvents: 'none',
        }}
      />
      {/* Diamond letter */}
      <div
        style={{
          position: 'relative',
          width: 64,
          height: 64,
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${PALETTE.gold}, ${PALETTE.goldDeep})`,
            transform: 'rotate(45deg)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(245,185,66,0.45)',
          }}
        />
        <span
          style={{
            position: 'relative',
            fontSize: 32,
            color: '#000',
            fontWeight: 900,
            fontFamily: '"Orbitron",sans-serif',
          }}
        >
          {letter}
        </span>
      </div>
      <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
        <div
          style={{
            fontSize: 24,
            fontWeight: 600,
            color: PALETTE.text,
            lineHeight: 1.3,
          }}
        >
          {text}
        </div>
        {showCounts && (
          <div
            style={{
              fontSize: 13,
              color: PALETTE.textDim,
              marginTop: 4,
              display: 'flex',
              gap: 12,
            }}
          >
            <span>{votes} votes</span>
            <span>{pct}%</span>
          </div>
        )}
      </div>
      {isReveal && isCorrect && (
        <div
          style={{
            position: 'relative',
            fontSize: 14,
            fontWeight: 800,
            color: PALETTE.green,
            letterSpacing: 2,
            textTransform: 'uppercase',
            padding: '6px 12px',
            background: 'rgba(34,197,94,0.18)',
            border: `1px solid ${PALETTE.green}`,
            borderRadius: 6,
          }}
        >
          Correct
        </div>
      )}
      {isReveal && isLocked && !isCorrect && (
        <div
          style={{
            position: 'relative',
            fontSize: 14,
            fontWeight: 800,
            color: PALETTE.red,
            letterSpacing: 2,
            textTransform: 'uppercase',
            padding: '6px 12px',
            background: 'rgba(239,68,68,0.18)',
            border: `1px solid ${PALETTE.red}`,
            borderRadius: 6,
          }}
        >
          Locked
        </div>
      )}
    </div>
  );
}

// ─── Payout banner ──────────────────────────────────────────────────────

function PayoutBanner({
  payout,
  isWin,
  prize,
}: {
  payout: NonNullable<GameSnapshot['payout']>;
  isWin: boolean;
  prize: number;
}) {
  if (!isWin || payout.winners === 0) {
    return (
      <div
        style={{
          padding: '20px 32px',
          background: 'linear-gradient(180deg, rgba(127,29,29,0.55), rgba(69,10,10,0.7))',
          border: `2px solid ${PALETTE.red}`,
          borderRadius: 18,
          textAlign: 'center',
          animation: 'hm-rise 400ms ease both',
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 800, color: PALETTE.red, letterSpacing: 2 }}>
          {payout.winners === 0 && isWin ? 'Nobody locked it in time' : 'Wrong answer — game over'}
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        padding: '20px 32px',
        background: 'linear-gradient(180deg, rgba(20,83,45,0.65), rgba(5,46,22,0.85))',
        border: `2px solid ${PALETTE.green}`,
        borderRadius: 18,
        animation: 'hm-rise 400ms ease both',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 12,
            letterSpacing: 4,
            color: PALETTE.green,
            textTransform: 'uppercase',
            fontWeight: 800,
          }}
        >
          Pot of {prize.toLocaleString()} 💎 split
        </div>
        <div style={{ fontSize: 30, fontWeight: 900, color: PALETTE.text, lineHeight: 1.2 }}>
          {payout.perVoter.toLocaleString()} 💎 each · {payout.winners} winners
        </div>
      </div>
      {payout.topWinners.length > 0 && (
        <div style={{ display: 'flex', gap: 12 }}>
          {payout.topWinners.map((w, i) => (
            <div
              key={`${w.username}-${i}`}
              style={{
                padding: '8px 14px',
                background: 'rgba(0,0,0,0.35)',
                border: `1px solid rgba(34,197,94,0.4)`,
                borderRadius: 8,
                fontSize: 14,
              }}
            >
              <div style={{ color: PALETTE.textDim, fontSize: 10, letterSpacing: 1 }}>#{i + 1}</div>
              <div style={{ fontWeight: 700 }}>{w.username}</div>
              <div style={{ color: PALETTE.gold, fontWeight: 700 }}>
                {w.gems.toLocaleString()} 💎
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ladder rail ────────────────────────────────────────────────────────

function LadderRail({ snapshot }: { snapshot: GameSnapshot }) {
  const ladder = snapshot.ladder;
  const current = snapshot.level;
  return (
    <aside
      style={{
        position: 'absolute',
        top: 40,
        right: 40,
        bottom: 40,
        width: 300,
        padding: '24px 20px',
        background: 'linear-gradient(180deg, rgba(11,21,48,0.85), rgba(2,6,23,0.95))',
        border: `2px solid rgba(245,185,66,0.45)`,
        borderRadius: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        boxShadow: '0 24px 64px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
      }}
    >
      <div
        style={{
          fontSize: 14,
          letterSpacing: 4,
          color: PALETTE.gold,
          textAlign: 'center',
          textTransform: 'uppercase',
          fontWeight: 800,
          marginBottom: 8,
        }}
      >
        Prize Ladder
      </div>
      {[...ladder].reverse().map((lvl) => {
        const isCurrent = lvl.level === current;
        const isCleared = lvl.level < current;
        return (
          <div
            key={lvl.level}
            style={{
              position: 'relative',
              padding: '8px 14px',
              borderRadius: 8,
              background: isCurrent
                ? `linear-gradient(90deg, ${PALETTE.gold}, ${PALETTE.goldDeep})`
                : isCleared
                  ? 'rgba(34,197,94,0.12)'
                  : 'rgba(255,255,255,0.04)',
              border: isCurrent
                ? `1px solid ${PALETTE.gold}`
                : isCleared
                  ? '1px solid rgba(34,197,94,0.35)'
                  : '1px solid rgba(255,255,255,0.06)',
              color: isCurrent ? '#000' : isCleared ? PALETTE.green : PALETTE.textDim,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              fontWeight: isCurrent ? 900 : 600,
              animation: isCurrent ? 'hm-glow 1.6s ease-in-out infinite' : undefined,
            }}
          >
            <span style={{ fontSize: 16, fontFamily: '"Orbitron",sans-serif' }}>Q{lvl.level}</span>
            <span style={{ fontSize: 16 }}>{lvl.prizeGems.toLocaleString()} 💎</span>
          </div>
        );
      })}
    </aside>
  );
}

// ─── Confetti / vignettes ───────────────────────────────────────────────

function Confetti() {
  const pieces = Array.from({ length: 80 });
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 800;
        const duration = 1800 + Math.random() * 1400;
        const isGold = i % 2 === 0;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: 0,
              left: `${left}%`,
              width: 10,
              height: 14,
              background: isGold ? PALETTE.gold : PALETTE.green,
              animation: `hm-confetti ${duration}ms linear ${delay}ms forwards`,
              borderRadius: 2,
            }}
          />
        );
      })}
    </div>
  );
}

function LoseVignette() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(ellipse at center, transparent 30%, rgba(127,29,29,0.55) 100%)',
        animation: 'hm-shake 600ms ease 1',
        pointerEvents: 'none',
        zIndex: 4,
      }}
    />
  );
}

// ─── End splash ─────────────────────────────────────────────────────────

function EndSplash({ snapshot }: { snapshot: GameSnapshot }) {
  const end = snapshot.endState;
  if (!end) return null;
  const isWin = end.reason === 'win';
  const isLose = end.reason === 'lose';
  const isNoVotes = end.reason === 'no-votes';
  const headline = isWin
    ? `🏆 JACKPOT 🏆`
    : isLose
      ? '💀 GAME OVER 💀'
      : isNoVotes
        ? '🦗 NOBODY VOTED 🦗'
        : '⏹ ENDED ⏹';
  const subline = isWin
    ? `Chat conquered all ${snapshot.totalLevels} questions`
    : `Made it to Q${end.finalLevel}`;
  const bgGrad = isWin
    ? 'radial-gradient(ellipse at center, rgba(20,83,45,0.85), rgba(5,46,22,0.95) 60%, #000 100%)'
    : isLose
      ? 'radial-gradient(ellipse at center, rgba(127,29,29,0.85), rgba(69,10,10,0.95) 60%, #000 100%)'
      : 'radial-gradient(ellipse at center, rgba(31,41,55,0.85), rgba(2,6,23,0.95) 60%, #000 100%)';
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: bgGrad,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'hm-fadeIn 600ms ease both',
      }}
    >
      <div
        style={{
          fontSize: 120,
          fontWeight: 900,
          letterSpacing: 8,
          fontFamily: '"Orbitron",sans-serif',
          color: isWin ? PALETTE.gold : isLose ? PALETTE.red : PALETTE.textDim,
          textShadow: isWin ? '0 0 64px rgba(245,185,66,0.85)' : '0 0 48px rgba(0,0,0,0.85)',
          animation: 'hm-pulse 1.4s ease-in-out infinite',
        }}
      >
        {headline}
      </div>
      <div style={{ fontSize: 36, color: PALETTE.text, marginTop: 16, fontWeight: 700 }}>
        {subline}
      </div>
      <div
        style={{
          fontSize: 28,
          color: PALETTE.gold,
          marginTop: 8,
          fontWeight: 700,
          fontFamily: '"Orbitron",sans-serif',
        }}
      >
        {end.totalGemsAwarded.toLocaleString()} 💎 paid out
      </div>
      {end.topPlayers.length > 0 && (
        <div
          style={{
            marginTop: 48,
            display: 'flex',
            gap: 16,
          }}
        >
          {end.topPlayers.map((p, i) => (
            <div
              key={`${p.username}-${i}`}
              style={{
                padding: '16px 24px',
                background: 'rgba(0,0,0,0.55)',
                border: `2px solid ${PALETTE.gold}`,
                borderRadius: 16,
                minWidth: 200,
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  color: PALETTE.gold,
                  letterSpacing: 4,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                }}
              >
                #{i + 1}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: PALETTE.text, marginTop: 4 }}>
                {p.username}
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, color: PALETTE.gold, marginTop: 4 }}>
                {p.gems.toLocaleString()} 💎
              </div>
              <div style={{ fontSize: 12, color: PALETTE.textDim, marginTop: 2 }}>
                {p.correct} correct
              </div>
            </div>
          ))}
        </div>
      )}
      {isWin && <Confetti />}
    </div>
  );
}
